import React from "react";
import { Character, AbilityName, SavingThrowName } from "./types";
import { StatCard } from "./StatCard";
import {
	getAbilityModifier,
	getSavingThrowModifier,
	formatAbilityName,
} from "./characterUtils";

interface CoreTabProps {
	character: Character;
	updateCharacter: (path: string, value: any) => void;
}

export const CoreTab: React.FC<CoreTabProps> = ({
	character,
	updateCharacter,
}) => (
	<div className="space-y-8">
		{/* Basic Info */}
		<StatCard title="Character Information">
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
				{[
					{ label: "Character Name", key: "name" },
					{ label: "Race", key: "race" },
					{ label: "Class", key: "class" },
					{ label: "Background", key: "background" },
					{ label: "Alignment", key: "alignment" },
				].map((field) => (
					<div key={field.key}>
						<label className="block text-sm font-semibold mb-2 text-gray-700">
							{field.label}
						</label>
						<input
							type="text"
							value={
								character[
									field.key as keyof Character
								] as string
							}
							onChange={(e) =>
								updateCharacter(field.key, e.target.value)
							}
							className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors duration-200"
							placeholder={`Enter ${field.label.toLowerCase()}`}
						/>
					</div>
				))}
				<div>
					<label className="block text-sm font-semibold mb-2 text-gray-700">
						Level
					</label>
					<input
						type="number"
						value={character.level}
						onChange={(e) =>
							updateCharacter(
								"level",
								parseInt(e.target.value) || 1
							)
						}
						className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors duration-200"
						min="1"
						max="20"
					/>
				</div>
			</div>
		</StatCard>

		{/* Abilities */}
		<StatCard title="Ability Scores">
			<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
				{Object.entries(character.abilities).map(([ability, score]) => {
					const modifier = getAbilityModifier(score);
					return (
						<div key={ability} className="text-center">
							<label className="block text-sm font-semibold mb-2 text-gray-700 capitalize">
								{ability}
							</label>
							<div className="bg-gradient-to-b from-indigo-100 to-indigo-200 rounded-2xl p-4 border-2 border-indigo-300">
								<input
									type="number"
									value={score}
									onChange={(e) =>
										updateCharacter(
											`abilities.${ability}`,
											parseInt(e.target.value) || 10
										)
									}
									className="w-full text-center text-lg font-bold bg-transparent border-none outline-none mb-2"
									min="1"
									max="30"
								/>
								<div
									className={`text-2xl font-black ${
										modifier >= 0
											? "text-green-600"
											: "text-red-600"
									}`}
								>
									{modifier >= 0 ? "+" : ""}
									{modifier}
								</div>
							</div>
						</div>
					);
				})}
			</div>
		</StatCard>

		{/* Proficiency & Inspiration */}
		<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
			<StatCard title="Proficiency Bonus">
				<input
					type="number"
					value={character.proficiencyBonus}
					onChange={(e) =>
						updateCharacter(
							"proficiencyBonus",
							parseInt(e.target.value) || 2
						)
					}
					className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors duration-200 text-center text-xl font-bold"
					min="2"
					max="6"
				/>
			</StatCard>
			<StatCard title="Inspiration">
				<label className="flex items-center justify-center gap-3 cursor-pointer">
					<input
						type="checkbox"
						checked={character.inspiration}
						onChange={(e) =>
							updateCharacter("inspiration", e.target.checked)
						}
						className="w-8 h-8 text-indigo-600 border-2 border-gray-300 rounded-lg focus:ring-indigo-500"
					/>
					<span
						className={`text-lg font-semibold ${
							character.inspiration
								? "text-indigo-600"
								: "text-gray-500"
						}`}
					>
						{character.inspiration ? "Inspired!" : "Not Inspired"}
					</span>
				</label>
			</StatCard>
		</div>

		{/* Saving Throws */}
		<StatCard title="Saving Throws">
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
				{Object.entries(character.abilities).map(([ability]) => {
					const abilityName = ability as SavingThrowName;
					const modifier = getSavingThrowModifier(
						character,
						abilityName
					);
					const isProficient =
						character.savingThrows[abilityName].proficient;
					return (
						<div
							key={ability}
							className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer hover:shadow-md ${
								isProficient
									? "bg-indigo-50 border-indigo-300"
									: "bg-gray-50 border-gray-200 hover:border-gray-300"
							}`}
							onClick={() =>
								updateCharacter(
									`savingThrows.${ability}.proficient`,
									!isProficient
								)
							}
						>
							<div className="flex items-center gap-3">
								<input
									type="checkbox"
									checked={isProficient}
									onChange={(e) => e.stopPropagation()}
									className="w-5 h-5 text-indigo-600 border-2 border-gray-300 rounded focus:ring-indigo-500"
								/>
								<span className="font-semibold text-gray-700">
									{formatAbilityName(abilityName)}:
								</span>
							</div>
							<div className="flex items-center gap-2">
								{isProficient && (
									<span className="text-xs bg-indigo-600 text-white px-2 py-1 rounded-full font-semibold">
										PROF
									</span>
								)}
								<span
									className={`font-bold text-lg ${
										modifier >= 0
											? "text-green-600"
											: "text-red-600"
									}`}
								>
									{modifier >= 0 ? "+" : ""}
									{modifier}
								</span>
							</div>
						</div>
					);
				})}
			</div>
			<div className="mt-4 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
				<strong>Tip:</strong> Click on a saving throw to toggle
				proficiency. Proficient saves add your proficiency bonus to the
				ability modifier.
			</div>
		</StatCard>
	</div>
);
